import { EmptyBox, FlexColumn, FlexRow } from 'components'
import { Icon, Label, Table, Card, Button, Checkbox } from 'semantic-ui-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { ChartUtility, StatusType, AuthService, getReportRowAuditId } from 'utility'
import { useAuth } from 'context'
import { PaginationComponent } from 'components/Pagination'
import { ClockCounterClockwise, Door, Download } from 'phosphor-react'
import { DesktopMode, MobileMode } from 'layout'
import AnalyzeWithAI from './AnalyzeWithAIModal'
import { AppPrimaryButton } from 'elements'
import { SendReportEmailModal } from './SendReportEmailModal'
import { EmailHistoryPanel } from './EmailHistoryPanel'

export const DownloadIcon = ({ outputFile }) => {
    if (!outputFile) return null

    return (
        <Label className="pointer" color="blue">
            <a
                href={outputFile}
                target="_blank"
                rel="noopener noreferrer"
                download
            >
                <FlexRow className="align-items-center">
                    <Download size={16} />
                </FlexRow>
            </a>
        </Label>
    )
}

const ContextMenu = styled.div`
    position: fixed;
    z-index: 1600;
    min-width: 188px;
    background: #ffffff;
    border: 1px solid #e6e8ef;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.16);
    padding: 4px;
`

const ContextItem = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    border: 0;
    background: transparent;
    border-radius: 8px;
    padding: 10px 12px;
    cursor: pointer;
    font: inherit;
    color: #1f2430;
    text-align: left;

    &:hover {
        background: #f4f6fb;
    }
`

const Row = ({
    item,
    onReopen,
    showSendEmailColumn,
    isSelectedForEmail,
    onToggleSendEmail,
    onEmailHistory,
    onRowContextMenu,
}) => {
    const { user } = useAuth()
    // eslint-disable-next-line no-unused-vars
    const isShareCompany = user?.IsShareCompany
    const isActionHandler = user?.CanCreateActionPlan === true
    const [openAI, setOpenAI] = useState(false)
    const auditId = getReportRowAuditId(item)
    return (
        <Table.Row onContextMenu={event => onRowContextMenu(event, item)}>
            {showSendEmailColumn && (
                <Table.Cell collapsing textAlign="center">
                    <Checkbox
                        checked={isSelectedForEmail}
                        onChange={(_, data) => onToggleSendEmail(item, data.checked)}
                    />
                </Table.Cell>
            )}
            <Table.Cell>
                <p>{item?.TemplateData?.AuditName}</p>
                {!!item?.ReportLink && (
                    <>
                        <Label className="pointer">
                            <a
                                href={item?.ReportLink}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Icon name="download" /> Download
                            </a>
                        </Label>
                        <Label color="blue" className="pointer ms-2" onClick={() => setOpenAI(true)}>
                            Analyze with AI
                        </Label>
                        <AnalyzeWithAI open={openAI} onClose={() => setOpenAI(false)} item={item} />
                    </>
                )}
                {!!auditId && (
                    <Label
                        color="teal"
                        className="pointer ms-2"
                        onClick={() => onEmailHistory(item)}
                    >
                        Email history
                    </Label>
                )}
            </Table.Cell>
            {user?.CompanyId !== 34930 && <Table.Cell>{item.CustomerName}</Table.Cell>}
            <Table.Cell>
                <FlexColumn>
                    {Number(item?.Status) === StatusType.Down
                        ? 'Down'
                        : item.StatusName}
                    {item?.Status === StatusType.Completed && (
                        <span>
                            {isActionHandler ? (
                                <AppPrimaryButton
                                    onClick={onReopen}
                                    className="pointer"
                                >
                                    Create Action Plan
                                </AppPrimaryButton>
                            ) : (
                                <Label
                                    color="red"
                                    onClick={onReopen}
                                    className="pointer"
                                >
                                    <FlexRow className="align-items-center">
                                        <Door size={14} />
                                        <span className="ms-2">Reopen</span>
                                    </FlexRow>
                                </Label>
                            )}
                        </span>
                    )}
                </FlexColumn>
            </Table.Cell>
            <Table.Cell>{item?.CreatedByName}</Table.Cell>
            <Table.Cell>
                {item?.CompletedOn &&
                    new Date(item?.CompletedOn).toLocaleString()}
            </Table.Cell>
            <Table.Cell>
                {
                    ChartUtility.getSectionResult(item?.TemplateData.Sections)
                        ?.total?.percentage
                }
            </Table.Cell>
        </Table.Row>
    )
}

const ReportCard = ({
    item,
    onReopen,
    showSendEmailColumn,
    isSelectedForEmail,
    onToggleSendEmail,
    onEmailHistory,
    onRowContextMenu,
}) => {
    const { user } = useAuth()
    const isActionHandler = user?.CanCreateActionPlan === true
    const [openAI, setOpenAI] = React.useState(false)
    const auditId = getReportRowAuditId(item)
    return (
        <Card
            fluid
            style={{ marginBottom: 16 }}
            onContextMenu={event => onRowContextMenu(event, item)}
        >
            <Card.Content>
                {showSendEmailColumn && (
                    <div className="d-flex justify-content-end mb-2">
                        <Checkbox
                            label="Send Email"
                            checked={isSelectedForEmail}
                            onChange={(_, data) =>
                                onToggleSendEmail(item, data.checked)
                            }
                        />
                    </div>
                )}
                <Card.Header>{item?.TemplateData?.AuditName}</Card.Header>
                <Card.Meta>
                    <div>Customer: {item.CustomerName}</div>
                    <div>
                        Status:{' '}
                        {Number(item?.Status) === StatusType.Down
                            ? 'Down'
                            : item.StatusName}
                    </div>
                    <div>Created By: {item?.CreatedByName}</div>
                    <div>
                        Completed On:{' '}
                        {item?.CompletedOn &&
                            new Date(item?.CompletedOn).toLocaleString()}
                    </div>
                    <div>
                        Score (%):{' '}
                        {
                            ChartUtility.getSectionResult(
                                item?.TemplateData.Sections,
                            )?.total?.percentage
                        }
                    </div>
                </Card.Meta>
                <Card.Description>
                    {!!item?.ReportLink && (
                        <Button
                            as="a"
                            href={item?.ReportLink}
                            target="_blank"
                            rel="noreferrer"
                            icon
                            labelPosition="left"
                            size="small"
                            style={{ marginBottom: 8 }}
                        >
                            <Icon name="download" /> Download
                        </Button>
                    )}
                    <Button
                        color="blue"
                        size="small"
                        onClick={() => setOpenAI(true)}
                        style={{ marginLeft: 8 }}
                    >
                        Analyze with AI
                    </Button>
                    {!!auditId && (
                        <Button
                            color="teal"
                            size="small"
                            onClick={() => onEmailHistory(item)}
                            style={{ marginLeft: 8 }}
                        >
                            Email history
                        </Button>
                    )}
                    <AnalyzeWithAI open={openAI} onClose={() => setOpenAI(false)} item={item} />
                    {item?.Status === StatusType.Completed && (
                        <div style={{ marginTop: 8 }}>
                            {isActionHandler ? (
                                <AppPrimaryButton
                                    color="blue"
                                    size="small"
                                    icon
                                    // labelPosition="left"
                                    onClick={onReopen}
                                >
                                    Create Action Plan
                                </AppPrimaryButton>
                            ) : (
                                <Button
                                    color="red"
                                    size="small"
                                    icon
                                    labelPosition="left"
                                    onClick={onReopen}
                                >
                                    <Door size={14} /> Reopen
                                </Button>
                            )}
                        </div>
                    )}
                </Card.Description>
            </Card.Content>
        </Card>
    )
}

const TableConatiner = styled.div`
    .label {
        a {
            opacity: 1;
        }
    }
`

export const ReportDataTable = ({
    data,
    loading,
    onReopen,
    pagination,
    pageChanged,
}) => {
    const user = AuthService.getUser()
    const companyId = user?.CompanyId || user?.Companies?.[0]?.CompanyId

    // Show "Send Email" column only for target companies (60551 and 34930)
    const showSendEmailColumn = useMemo(
        () => companyId === 60551 || companyId === 34930,
        [companyId],
    )

    const [selectedTemplatesForEmail, setSelectedTemplatesForEmail] =
        useState([])
    const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false)
    const [historyAuditId, setHistoryAuditId] = useState('')
    const [contextMenu, setContextMenu] = useState(null)
    const contextMenuRef = useRef(null)

    const openEmailHistory = item => {
        const auditId = getReportRowAuditId(item)
        if (!auditId) return
        setContextMenu(null)
        setHistoryAuditId(auditId)
    }

    const onRowContextMenu = (event, item) => {
        const target =
            event.target?.nodeType === 1
                ? event.target
                : event.target?.parentElement
        if (target?.closest?.('a, button, input, label')) return
        const auditId = getReportRowAuditId(item)
        if (!auditId) return
        event.preventDefault()
        const menuWidth = 200
        const menuHeight = 52
        setContextMenu({
            x: Math.max(8, Math.min(event.clientX, window.innerWidth - menuWidth - 8)),
            y: Math.max(8, Math.min(event.clientY, window.innerHeight - menuHeight - 8)),
            auditId,
        })
    }

    useEffect(() => {
        if (!contextMenu) return undefined
        const onPointerDown = event => {
            if (contextMenuRef.current?.contains(event.target)) return
            setContextMenu(null)
        }
        const onKey = event => {
            if (event.key === 'Escape') setContextMenu(null)
        }
        document.addEventListener('mousedown', onPointerDown)
        document.addEventListener('keydown', onKey)
        window.addEventListener('scroll', onPointerDown, true)
        return () => {
            document.removeEventListener('mousedown', onPointerDown)
            document.removeEventListener('keydown', onKey)
            window.removeEventListener('scroll', onPointerDown, true)
        }
    }, [contextMenu])

    const handleToggleSendEmail = (item, checked) => {
        setSelectedTemplatesForEmail(prev => {
            if (checked) {
                if (prev.find(x => x.TaskId === item.TaskId)) return prev
                return [...prev, item]
            }
            return prev.filter(x => x.TaskId !== item.TaskId)
        })
    }

    const handleOpenSendEmailModal = () => {
        if (!selectedTemplatesForEmail.length) return
        setSendEmailModalOpen(true)
    }

    const handleCloseSendEmailModal = () => {
        setSendEmailModalOpen(false)
    }

    const currentPageItems = data || []
    const isAllCurrentPageSelectedForEmail =
        currentPageItems.length > 0 &&
        currentPageItems.every(item =>
            selectedTemplatesForEmail.some(x => x.TaskId === item.TaskId),
        )
    const isSomeCurrentPageSelectedForEmail =
        currentPageItems.some(item =>
            selectedTemplatesForEmail.some(x => x.TaskId === item.TaskId),
        ) && !isAllCurrentPageSelectedForEmail

    const handleSelectAllSendEmail = (_, { checked }) => {
        if (checked) {
            setSelectedTemplatesForEmail(prev => {
                const existingIds = new Set(prev.map(x => x.TaskId))
                const toAdd = currentPageItems.filter(
                    item => !existingIds.has(item.TaskId),
                )
                return [...prev, ...toAdd]
            })
            return
        }
        const currentPageIds = new Set(
            currentPageItems.map(item => item.TaskId),
        )
        setSelectedTemplatesForEmail(prev =>
            prev.filter(x => !currentPageIds.has(x.TaskId)),
        )
    }

    return (
        <TableConatiner>
            <DesktopMode>
                {showSendEmailColumn && selectedTemplatesForEmail.length > 0 && (
                    <div className="w-100 d-flex justify-content-end mb-2">
                        <AppPrimaryButton onClick={handleOpenSendEmailModal}>
                            Send Email
                        </AppPrimaryButton>
                    </div>
                )}
                <Table celled>
                    <Table.Header>
                        <Table.Row active>
                            {showSendEmailColumn && (
                                <Table.HeaderCell
                                    textAlign="center"
                                    style={{ whiteSpace: 'nowrap', minWidth: 130 }}
                                >
                                    <div className="d-flex flex-column align-items-center gap-1">
                                        <span>Send Email</span>
                                        <Checkbox
                                            checked={isAllCurrentPageSelectedForEmail}
                                            indeterminate={
                                                isSomeCurrentPageSelectedForEmail
                                            }
                                            onChange={handleSelectAllSendEmail}
                                        />
                                    </div>
                                </Table.HeaderCell>
                            )}
                            <Table.HeaderCell textAlign="center">
                                {companyId === 34930 ? 'Template' : 'Audit Name'}
                            </Table.HeaderCell>
                            {companyId !== 34930 && <Table.HeaderCell textAlign="center">
                                Customer Name
                            </Table.HeaderCell>}
                            <Table.HeaderCell textAlign="center">
                                Status
                            </Table.HeaderCell>
                            <Table.HeaderCell textAlign="center">
                                Created By
                            </Table.HeaderCell>
                            <Table.HeaderCell textAlign="center">
                                Completed On
                            </Table.HeaderCell>
                            <Table.HeaderCell textAlign="center">
                                Score (%)
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {data?.map(item => (
                            <React.Fragment key={item.TaskId}>
                                <Row
                                    item={item}
                                    onReopen={() => onReopen(item)}
                                    showSendEmailColumn={showSendEmailColumn}
                                    isSelectedForEmail={!!selectedTemplatesForEmail.find(x => x.TaskId === item.TaskId)}
                                    onToggleSendEmail={handleToggleSendEmail}
                                    onEmailHistory={openEmailHistory}
                                    onRowContextMenu={onRowContextMenu}
                                />
                            </React.Fragment>
                        ))}
                    </Table.Body>
                </Table>
                {!!data?.length && !loading && (
                    <div className="w-100 justify-content-end d-flex mt-3">
                        <PaginationComponent
                            activePage={pagination.currentPage}
                            pageChanged={pageChanged}
                            pageSize={pagination.perPage}
                            totalCount={pagination.total}
                        />
                    </div>
                )}
                {(!data || !data.length) && !loading && <EmptyBox />}
            </DesktopMode>
            <MobileMode>
                {showSendEmailColumn && currentPageItems.length > 0 && (
                    <div className="w-100 d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                        <Checkbox
                            label="Select all on this page"
                            checked={isAllCurrentPageSelectedForEmail}
                            indeterminate={isSomeCurrentPageSelectedForEmail}
                            onChange={handleSelectAllSendEmail}
                        />
                        {selectedTemplatesForEmail.length > 0 && (
                            <AppPrimaryButton onClick={handleOpenSendEmailModal}>
                                Send Email
                            </AppPrimaryButton>
                        )}
                    </div>
                )}
                {data?.length
                    ? data.map(item => (
                        <ReportCard
                            key={item.TaskId}
                            item={item}
                            onReopen={() => onReopen(item)}
                            showSendEmailColumn={showSendEmailColumn}
                            isSelectedForEmail={!!selectedTemplatesForEmail.find(x => x.TaskId === item.TaskId)}
                            onToggleSendEmail={handleToggleSendEmail}
                            onEmailHistory={openEmailHistory}
                            onRowContextMenu={onRowContextMenu}
                        />
                    ))
                    : !loading && <EmptyBox />}
                {!!data?.length && !loading && (
                    <div className="w-100 justify-content-end d-flex mt-3">
                        <PaginationComponent
                            activePage={pagination.currentPage}
                            pageChanged={pageChanged}
                            pageSize={pagination.perPage}
                            totalCount={pagination.total}
                        />
                    </div>
                )}
            </MobileMode>

            {showSendEmailColumn && selectedTemplatesForEmail.length > 0 && (
                <SendReportEmailModal
                    open={sendEmailModalOpen}
                    onClose={handleCloseSendEmailModal}
                    selectedReports={selectedTemplatesForEmail}
                    onRemoveReport={report =>
                        handleToggleSendEmail(report, false)
                    }
                />
            )}
            {contextMenu &&
                createPortal(
                    <ContextMenu
                        ref={contextMenuRef}
                        style={{ top: contextMenu.y, left: contextMenu.x }}
                    >
                        <ContextItem
                            type="button"
                            onClick={() => {
                                setHistoryAuditId(contextMenu.auditId)
                                setContextMenu(null)
                            }}
                        >
                            <ClockCounterClockwise size={16} />
                            Email history
                        </ContextItem>
                    </ContextMenu>,
                    document.body,
                )}
            {!!historyAuditId && (
                <EmailHistoryPanel
                    open
                    showSendEmail
                    auditId={historyAuditId}
                    onClose={() => setHistoryAuditId('')}
                />
            )}
        </TableConatiner>
    )
}
